import { ChangeEventHandler, useEffect, useMemo } from 'react';

import { Flex, FlexProps, HStack, StyleProps } from '@chakra-ui/react';
import { useFormikContext } from 'formik';

import { Deal } from '../../../gql/dealGql';
import { ProcessorEnum } from '../../../gql/financialInfoGql';

import DatePicker from '../../shared/DatePicker';
import Select from '../../shared/Select';
import {
  CUSTOM_DATE_FIELDS,
  CustomDateFieldsKey,
  processorSectionCustomDateFields,
} from '../constants';

import InputRow from './InputRow';
import { ResetProcessorButton } from './buttons/ResetProcessorButton';
import { SendToProcessorButton } from './buttons/SendToProcessorButton';

import { LDFlags } from '../../../constants/experiments';
import useFlag from '../../../hooks/useFlag';
import { processorOptions } from '../../../utils/deals';

type ProcessorSectionProps = FlexProps & {
  saveCustomDate: (fieldName: string, value: string) => Promise<void>;
  saveProcessor: (value?: ProcessorEnum) => Promise<void>;
  defaultFontSize?: StyleProps['fontSize'];
  customHandleChange?: ChangeEventHandler<HTMLInputElement | HTMLSelectElement>;
};

export const ProcessorSection = ({
  saveCustomDate,
  saveProcessor,
  defaultFontSize = 'sm',
  customHandleChange,
  ...rest
}: ProcessorSectionProps) => {
  const { initialValues, values, touched } = useFormikContext<Deal>();

  useEffect(() => {
    if (
      initialValues.financial_info.processor === values.financial_info.processor &&
      !touched.financial_info?.processor
    ) {
      return;
    }

    saveProcessor(values.financial_info.processor);
  }, [values.financial_info.processor]);

  // Remove processor Tater Title if the flag is not enabled
  // Feature‑gate "Tater Title" option
  const isTaterTitleEnabled = useFlag(LDFlags.ADD_TATER_TITLE_PROCESSOR);
  const options = useMemo(
    () =>
      isTaterTitleEnabled
        ? processorOptions
        : processorOptions.filter((p) => p.value !== ProcessorEnum.TaterTitle),
    [isTaterTitleEnabled],
  );

  return (
    <Flex direction="column" {...rest}>
      <InputRow label="Processor">
        <Select
          name="financial_info.processor"
          size="sm"
          options={options}
          onChange={customHandleChange}
        />
      </InputRow>
      <HStack w="full" justify="end" mb={2}>
        <ResetProcessorButton />
        <SendToProcessorButton />
      </HStack>
      {Object.entries(processorSectionCustomDateFields).map(([fieldKey, label]) => (
        <InputRow
          key={fieldKey}
          label={label}
          labelProps={{
            whiteSpace: 'normal',
          }}
        >
          <DatePicker
            name={CUSTOM_DATE_FIELDS[fieldKey as CustomDateFieldsKey]}
            boxStyles={{
              w: '100%',
            }}
            inputProps={{
              fontSize: defaultFontSize,
            }}
            additionalHandleChange={(e) =>
              saveCustomDate(e.currentTarget.name, e.currentTarget.value)
            }
          />
        </InputRow>
      ))}
    </Flex>
  );
};
